import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { formatLocalDate, formatLocalDateTime } from "../../utils/formatDate";
import { CustomTable } from "../../componentes/table/CustomTable";
import { Dialog } from "primereact/dialog";
import CadastrarAjusteComissao from "../comissoes-motoristas/ajustes/CadastrarAjuste";
import CadastroAjusteDL from "../comissoes-motoristas/ajustes/CadastroAjusteDL";

const fmt = (v) => formatLocalDateTime(v);
const fmtDate = (v) => formatLocalDate(v);
const fmtNum = (v, d = 2) => v != null ? Number(v).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—";
const fmtBool = (v) => v === true
    ? <i className="fa fa-check-circle text-success" />
    : v === false
        ? <i className="fa fa-times-circle text-destructive/70" />
        : <span className="text-muted-foreground/50">—</span>;

function PrazoBadge({ value, onClickForaPrazo }) {
    const [hover, setHover] = useState(false);

    if (!value) {
        return <span className="text-muted-foreground text-[10px]">—</span>;
    }

    const map = {
        NO_PRAZO: { label: "No prazo", cls: "bg-success/10 text-success" },
        FORA_DO_PRAZO: { label: "Fora do Prazo", cls: "bg-destructive/10 text-destructive" },
        INDEFINIDO: { label: "Indefinido", cls: "bg-muted text-muted-foreground" },
    };

    const cfg = map[value] ?? {
        label: value,
        cls: "bg-muted text-muted-foreground",
    };

    const isForaPrazo = value === "FORA_DO_PRAZO";

    // 👉 Se for fora do prazo e estiver em hover, vira botão
    if (isForaPrazo && hover) {
        return (
            <button
                onClick={onClickForaPrazo}
                onMouseLeave={() => setHover(false)}
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success hover:brightness-95 text-success-foreground transition-colors duration-150"
            >
                Solic. Ajustar
            </button>
        );
    }

    // 👉 Caso normal (badge)
    return (
        <span
            onMouseEnter={() => isForaPrazo && setHover(true)}
            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.cls} ${isForaPrazo ? "cursor-pointer" : ""}`}
        >
            {cfg.label}
        </span>
    );
}

function AjusteBadge({ value }) {

    if (!value) {
        return <span className="text-muted-foreground text-[10px]">—</span>;
    }

    const map = {
        APROVADO: { label: "Ajustado Manualmente", cls: "bg-success/10 text-success" },
        REJEITADO: { label: "Rejeitado", cls: "bg-destructive/10 text-destructive" },
        PENDENTE: { label: "Pendente", cls: "bg-yellow-100 text-yellow-700" },
    };

    const cfg = map[value] ?? {
        label: value,
        cls: "bg-muted text-muted-foreground",
    };

    const isForaPrazo = value === "FORA_DO_PRAZO";

    // 👉 Caso normal (badge)
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.cls} ${isForaPrazo ? "cursor-pointer" : ""}`}
        >
            {cfg.label}
        </span>
    );
}


function StatCard({ icon, label, value, color = "teal" }) {
    const colors = {
        teal: "bg-teal-50   border-teal-200   text-teal-700",
        blue: "bg-blue-50   border-blue-200   text-blue-700",
        green: "bg-green-50  border-green-200  text-green-700",
        amber: "bg-amber-50  border-amber-200  text-amber-700",
        rose: "bg-rose-50   border-rose-200   text-rose-700",
        violet: "bg-violet-50 border-violet-200 text-violet-700",
        orange: "bg-orange-50 border-orange-200 text-orange-700",
    };
    return (
        <div className={`rounded-xl border p-3 flex items-center gap-3 ${colors[color]}`}>
            <i className={`${icon} text-lg w-5 text-center flex-shrink-0`} />
            <div>
                <p className="text-[11px] opacity-70 leading-tight">{label}</p>
                <p className="text-sm font-bold leading-tight">{value}</p>
            </div>
        </div>
    );
}

export default function RotasGreenmile({ rotas, ano, mes, loading }) {

    const [showDialog, setShowDialog] = useState(false);
    const [ajusteData, setAjusteData] = useState(null);

    function segParaHoras(segundos) {
        if (segundos == null) return "—";

        const s = Math.floor(segundos);
        const horas = Math.floor(s / 3600);
        const minutos = Math.floor((s % 3600) / 60);
        const segs = s % 60;

        return `${horas.toString().padStart(2, "0")}:${minutos
            .toString()
            .padStart(2, "0")}:${segs.toString().padStart(2, "0")}`;
    }


    // ── Stats ──────────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        if (!rotas.length) return null;
        const total = rotas.length;
        const comImagem = rotas.filter(r => r.imagens === true).length;
        const comGps = rotas.filter(r => r.gps === true).length;
        const noPrazo = rotas.filter(r => r.entregueNoPrazo === "NO_PRAZO").length;
        const atrasadas = rotas.filter(r => r.entregueNoPrazo === "ATRASADO").length;
        const adiantadas = rotas.filter(r => r.entregueNoPrazo === "ADIANTADO").length;
        const cargaVinculada = rotas.filter(r => r.cargaEntregaVinculada === true).length;
        const cargaNaoVinculada = rotas.filter(r => r.cargaEntregaVinculada === false).length;
        const totalVol = rotas.reduce((a, r) => a + (Number(r.volumeRealizado) || 0), 0);
        const totalDist = rotas.reduce((a, r) => a + (Number(r.distanciaRealizada) || 0), 0);
        return { total, comImagem, comGps, noPrazo, atrasadas, adiantadas, totalVol, totalDist, cargaVinculada, cargaNaoVinculada };
    }, [rotas]);

    // ── Columns ────────────────────────────────────────────────────────────
    const columns = useMemo(() => [
        {
            key: "filialNome",
            label: "Filial",
            sortable: true,
        },
        {
            key: "carga",
            label: "Carga",
            sortable: true,
            className: "font-semibold",
        },
        {
            key: "transporte",
            label: "Transporte",
            sortable: true,
            center: true,
        },
        {
            key: "veiculoPlaca",
            label: "Placa",
            sortable: true,
            center: true,
        },
        {
            key: "codigoMotorista",
            label: "Motorista",
            sortable: true,
            center: true,
        },
        {
            key: "data",
            label: "Data Op.",
            sortable: true,
            center: true,
            render: (v) => fmtDate(v),
        },
        {
            key: "entregueNoPrazo",
            label: "Prazo",
            sortable: true,
            center: true,
            render: (_, row) => (
                <PrazoBadge
                    value={row.entregueNoPrazo}
                    onClickForaPrazo={() => {
                        setAjusteData({
                            filialId: row.filialId,
                            transporte: row.transporte,
                            tipo: "IGNORAR_GREENMILE"
                        });
                        setShowDialog(true);
                    }}
                />
            ),
        },
        {
            key: "cargaEntregaVinculada",
            label: "Escala Vinc.",
            sortable: false,
            filter: false,
            center: true,
            className: "bg-primary/5",
            render: (v) => fmtBool(v)
        },
        {
            key: "imagens",
            label: "Imagens",
            sortable: true,
            filter: false,
            center: true,
            className: "bg-primary/5",
            render: (v) => fmtBool(v),
        },
        {
            key: "gps",
            label: "GPS",
            sortable: true,
            filter: false,
            center: true,
            className: "bg-primary/5",
            render: (v) => fmtBool(v),
        },
        {
            key: "saidaPlanejadaOriginal",
            label: "Saída Planejada",
            sortable: true,
            center: true,
            className: "bg-muted",
            render: (v) => fmt(v),
        },
        {
            key: "chegadaPlanejadaOriginal",
            label: "Chegada Planejada",
            sortable: true,
            center: true,
            className: "bg-muted",
            render: (v) => fmt(v),
        },
        {
            key: "saidaRealizada",
            label: "Saída Realizada",
            sortable: true,
            center: true,
            className: "bg-muted",
            render: (v) => fmt(v),
        },

        {
            key: "chegadaRealizada",
            label: "Chegada Realizada",
            sortable: true,
            center: true,
            className: "bg-muted",
            render: (v) => fmt(v),
        },
        {
            key: "volumePlanejado",
            label: "Vol. Planejado",
            sortable: true,
            center: true,
            className: "bg-teal-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "volumeRealizado",
            label: "Vol. Realizado",
            sortable: true,
            center: true,
            className: "bg-teal-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "distanciaPlanejadaOriginal",
            label: "Dist. Planejada",
            sortable: true,
            center: true,
            className: "bg-violet-50",
            render: (v) => v != null ? `${fmtNum(v)} km` : "—",
        },
        {
            key: "distanciaRealizada",
            label: "Dist. Realizada",
            sortable: true,
            center: true,
            className: "bg-violet-50",
            render: (v) => v != null ? `${fmtNum(v)} km` : "—",
        },
        {
            key: "tempoTotalRotaOriginal",
            label: "Tempo Planejado",
            sortable: true,
            center: true,
            className: "bg-amber-50",
            render: (v) => v != null ? `${segParaHoras(v)}` : "—",
        },
        {
            key: "tempoTotalRotaRealizado",
            label: "Tempo Realizado",
            sortable: true,
            center: true,
            className: "bg-amber-50",
            render: (v) => v != null ? `${segParaHoras(v)}` : "—",
        },
        {
            key: "totalEventosEmAberto",
            label: "Eventos",
            sortable: true,
            center: true,
            render: (v) => v != null
                ? <span className={v > 0 ? "text-orange-600 font-bold" : "text-muted-foreground"}>{v}</span>
                : "—",
        },
        {
            key: "ajusteComissaoStatus",
            label: "Prazo",
            sortable: true,
            center: true,
            render: (i) => <AjusteBadge value={i} />,
        },
    ], []);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-background">

            {/* Sub-header com filtros e stats */}
            <div className="bg-card border-b border-border px-4 py-3 space-y-3">

                {/* KPIs */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
                        <StatCard icon="fa fa-road" label="Total Rotas" value={stats.total} color="blue" />
                        <StatCard icon="fa fa-check-circle" label="No Prazo" value={stats.noPrazo} color="green" />
                        <StatCard icon="fa fa-times" label="Sem Escalas de Rotas" value={stats.cargaNaoVinculada} color="rose" />
                        <StatCard icon="fa fa-fast-forward" label="Escalas Vinculadas" value={stats.cargaVinculada} color="amber" />
                        <StatCard icon="fa fa-camera" label="Com Imagem" value={stats.comImagem} color="violet" />
                        <StatCard icon="fa fa-cube" label="Vol. Realizado" value={`${fmtNum(stats.totalVol)} m³`} color="teal" />
                        <StatCard icon="fa fa-arrows-h" label="Dist. Total" value={`${fmtNum(stats.totalDist)} km`} color="orange" />
                    </div>
                )}
            </div>

            {/* Tabela */}
            <CustomTable
                data={rotas}
                columns={columns}
                showHeader={false}
                loading={loading}
                maxHeight="calc(100vh - 220px)"
                className="text-[11px]"
                pagination
                exportable
                exportFilename={`Base Greenmile ${ano}-${mes}`}
                pageSize={20}
                footer={(rows) => {
                    const totalVol = rows.reduce((a, r) => a + (Number(r.volumeRealizado) || 0), 0);
                    const totalDist = rows.reduce((a, r) => a + (Number(r.distanciaRealizada) || 0), 0);
                    const noPrazo = rows.filter(r => r.entregueNoPrazo === "NO_PRAZO").length;
                    const comImg = rows.filter(r => r.imagens === true).length;
                    const pctPrazo = rows.length > 0 ? ((noPrazo / rows.length) * 100).toFixed(1) : "0.0";
                    const pctImg = rows.length > 0 ? ((comImg / rows.length) * 100).toFixed(1) : "0.0";

                    return (
                        <div className="flex flex-wrap justify-end gap-5 p-2 text-xs font-semibold text-foreground">
                            <span className="text-primary">Rotas: {rows.length}</span>
                            <span className="text-success">No Prazo: {noPrazo} ({pctPrazo}%)</span>
                            <span className="text-violet-600">Com Imagem: {comImg} ({pctImg}%)</span>
                            <span className="text-teal-700">Vol. Realizado: {fmtNum(totalVol)} m³</span>
                            <span className="text-orange-600">Dist. Realizada: {fmtNum(totalDist)} km</span>
                        </div>
                    );
                }}
            />

            {showDialog && (
                <Dialog
                    header="Solicitar ajuste de comissão"
                    visible={showDialog}
                    onHide={() => setShowDialog(false)}
                    className="w-[70%]"
                >
                    <CadastroAjusteDL
                        defaultValues={ajusteData}
                        showFilial={false}
                        showTransporte={false}
                        onClose={() => setShowDialog(false)}
                        onSuccess={() => {
                            setShowDialog(false);
                            // opcional: reload
                        }}
                    />
                </Dialog>
            )}

        </motion.div >
    );
}
